// CompanySelection.tsx - Updated to match CompanyManagement table styling
import React, { useState, useMemo } from 'react';
import './FunctionsManagement.css'; 
import { Company, FunctionsManagementProps } from './types';

interface CompanySelectionProps extends FunctionsManagementProps {
  companies: Company[];
  isLoadingCompanies: boolean;
  onCompanySelect: (company: Company) => void;
}

export const CompanySelection: React.FC<CompanySelectionProps> = ({
  companies,
  isLoadingCompanies,
  onCompanySelect
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Filter companies based on search term
  const filteredCompanies = useMemo(() => {
    if (!searchTerm.trim()) {
      return companies;
    }

    const term = searchTerm.toLowerCase().trim();
    
    return companies.filter(company => 
      company.companyName.toLowerCase().includes(term) ||
      (company.companyDescription && company.companyDescription.toLowerCase().includes(term))
    );
  }, [companies, searchTerm]);

  // Clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  if (isLoadingCompanies) {
    return (
      <div className="loading">
        <span className="spinner"></span>
        Loading companies...
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="company-management">
        <div className="no-companies">
          <div className="no-data-icon">🏢</div>
          <h3>No Companies Found</h3>
          <p>No companies are available. Please add companies first before managing functions.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="company-management">
      <div className="companies-container">
        {/* Search Bar Section */}
        <div className="search-section">
          <div className="search-input-group">
            <div className="search-field">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                placeholder="Find companies"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button 
                  onClick={clearSearch}
                  className="clear-search-btn"
                  title="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
            
            {searchTerm && (
              <div className="search-results-info">
                Found {filteredCompanies.length} of {companies.length} companies
              </div>
            )}
          </div>
        </div>

        <div className="companies-table-container">
          <table className="minimalist-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>COMPANY NAME</th>
                <th>DESCRIPTION</th>
                <th>SELECT</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="no-results">
                    <div className="no-results-content">
                      <span className="no-results-icon">🔍</span>
                      <p>No companies found matching "{searchTerm}"</p>
                      <button onClick={clearSearch} className="clear-search-link">
                        Clear search to see all companies
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company) => (
                  <tr key={company.companyId} className="company-row">
                    <td className="org-id">#{company.companyId}</td>
                    <td className="org-name">
                      <div className="name-cell">
                        <div className="org-name-text">{company.companyName}</div>
                      </div>
                    </td>
                    <td className="company-description-cell">
                      <div className="description-text">
                        {company.companyDescription ? 
                          (company.companyDescription.length > 80 ? 
                            company.companyDescription.substring(0, 80) + '...' : 
                            company.companyDescription
                          ) : 
                          <span className="no-description">No description available</span>
                        }
                      </div>
                    </td>
                    <td className="company-select">
                      <button
                        onClick={() => onCompanySelect(company)}
                        className="select-company-btn"
                        title={`Select ${company.companyName}`}
                      >
                        Select
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};